import {useRef} from 'react';

import {Alert, Button, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {useVerifiedPermissions} from '@flexinet/auth-components';

import {useBillingProviders} from '../hooks/useBillingProviders.ts';
import {useBillingProvidersSelection} from '../hooks/useBillingProvidersSelection.ts';
import {useBillingProvidersTableColumns} from '../hooks/useBillingProvidersTableColumns.tsx';
import {useBillingProviderStatusToggle} from '../hooks/useBillingProviderStatusToggle.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {BillingProvider} from '../types';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

// The message for an empty table. THREE different situations (§3, §13), and they must
// not read alike: nothing matches the search, the status filter hides everything loaded,
// or there are no providers at all.
const emptyState = (term: string, filters: Record<string, string>, moreToLoad: boolean) => {
    const more = moreToLoad
        ? ` ${I18n.get('More providers may be on a page that has not been loaded yet.')}`
        : '';
    if (term) {
        return {
            color: 'gray',
            title: I18n.get('No matching billing providers'),
            body: `${I18n.get('Nothing in the loaded billing providers matches')} "${term}". ${I18n.get('Clear the search to see every loaded row.')}${more}`,
        };
    }
    if (Object.keys(filters).length > 0) {
        return {
            color: 'gray',
            title: I18n.get('No billing providers match the filter'),
            body: `${I18n.get('The filter')} (${Object.entries(filters)
                                                         .map(([key, value]) => `${key}: ${value}`)
                                                         .join(', ')}) ${I18n.get('hides every loaded provider. Open Filter and choose Clear to see all statuses.')}${more}`,
        };
    }
    return {
        color: 'blue',
        title: I18n.get('No billing providers yet'),
        body: I18n.get('A billing provider is a channel customers are billed through, such as Stripe or PayPal. Use Add Billing Provider to create the first one.'),
    };
};

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useBillingProvidersSelection();
    const {allowedActions} = useVerifiedPermissions();
    const {toggleStatus, pendingId} = useBillingProviderStatusToggle();

    const {
        filteredItems,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchMoreRecords,
        handleFilterByKeyword
    } = useBillingProviders();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const {columns} = useBillingProvidersTableColumns();

    // Enable/Disable change the provider's status, the field the edit form gates on
    // UpdateBillingProvider, so they share that gate and are HIDDEN without it (§10). There
    // is no separate Enable/Disable AVP action. Not sortable: there is nothing to sort by.
    const canUpdate = allowedActions.includes(rbacActions.UpdateBillingProvider);
    const actionsColumn: DataTableColumn<BillingProvider> = {
        accessor: 'actions',
        title: I18n.get('Actions'),
        render: (record: BillingProvider) => (
            <Button size='xs'
                    variant={record.status === 'Enabled' ? 'outline' : 'filled'}
                    color={record.status === 'Enabled' ? 'red' : undefined}
                    loading={pendingId === record.id}
                    aria-label={`${record.status === 'Enabled' ? I18n.get('Disable') : I18n.get('Enable')} ${record.name ?? record.key}`}
                    onClick={() => toggleStatus(record)}>
                {record.status === 'Enabled' ? I18n.get('Disable') : I18n.get('Enable')}
            </Button>
        ),
    };

    const term = keyword.trim();
    const empty = emptyState(term, filters, nextToken !== null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load billing providers')}>
                    {I18n.get('The billing API did not return the billing providers list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<BillingProvider>
                columns={canUpdate ? [...columns, actionsColumn] : columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchMoreRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              Gated on !loading so it does not flash during the first fetch, and on !isError
              so a failed load keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert color={empty.color} title={empty.title}>
                    {empty.body}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={filteredItems.length}
                filterTerm={keyword}
                noun={'billing provider'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchMoreRecords}
            />
        </SimpleGrid>
    );
};
